/**
 * df-b99ce9dcabce: how a background task that nobody awaits tells "the data is not reachable
 * right now" apart from a failure somebody should hear about.
 *
 * The paid-user background tasks (publishing unpublished foods, retrying coach notifications,
 * checking the auto-plan recipe copy) are started with `void` on every app start and warm
 * resume, so a rejection they let out lands in the global unhandled-rejection handler. They
 * used to catch only `DatabaseUnavailableError`. A remote read that CouchDB refuses — a 401 or
 * 403 while the session is being re-established on resume — is not that error: PouchDB rejects
 * with its own plain `{ status, name, error, reason }` object, which escaped and was reported
 * as `UnhandledCouchRejection 401 unauthorized` (Sentry CYBERNETIC-FITNESS-APP-46C).
 *
 * Every one of those tasks runs again at the next app start, so a not-ready condition costs
 * nothing worth reporting. Anything else is still reported by the caller. Other CouchDB
 * statuses (a 404 or a 500 from these reads) are deliberately NOT on the not-ready list: they
 * say something is wrong, not that the session is still being set up.
 *
 * Kept in its own module, with no Angular imports, so the node test runner can load it:
 * `food-db.service.ts` cannot be loaded there, and this is the decision its catch makes.
 */

import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { isCouchHttpErrorObject, isOfflineOrTransientNetworkError } from 'services/error-patterns';

/** CouchDB refusing the session: not signed in yet (401) or not allowed yet (403). */
const REFUSED_SESSION_STATUSES = [401, 403];

/**
 * True when a background read failed because the data is not reachable right now: the local
 * database is unavailable or closing, the device is offline, or CouchDB refused the session.
 * The task should stop quietly and let the next app start retry it.
 */
export function isBackgroundReadNotReady(err: unknown): boolean {
  if (isDatabaseUnavailable(err)) return true;
  if (isOfflineOrTransientNetworkError(err)) return true;
  return isCouchHttpErrorObject(err) && REFUSED_SESSION_STATUSES.includes(err.status);
}
